import {AgGridReact} from "@ag-grid-community/react";
import log from "loglevel";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useGridRefreshOnInterval} from "../../../hooks/useGridRefreshOnInterval";
import {ApolloError, ApolloQueryResult, gql, useApolloClient} from "@apollo/client";
import {
  CellClickedEvent,
  ColDef,
  ColumnMovedEvent,
  ColumnState,
  ColumnVisibleEvent,
  FilterChangedEvent,
  GetContextMenuItemsParams,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  ITextFilterParams,
  MenuItemDef,
  ModuleRegistry,
  PaginationChangedEvent,
  RowClassParams,
  RowStyle,
  SortChangedEvent,
  ValueGetterParams
} from "@ag-grid-community/core";
import {MenuModule} from "@ag-grid-enterprise/menu";
import {
  ModelStringFilterInput,
  StopFilter,
  useGetEtaLazyQuery,
  useSaveClearJobStopsMutation,
  useSetUserPrefMutation
} from "../../../generated/graphql";
import {
  createStopsServerSideDatasource,
  GridContext
} from "../../../services/datasources/StopsServerSideDataSource.service";
import {useAuthState} from "../../../providers/AuthProvider";
import {
  addNewColumnStateAfter,
  getColorPriority,
  getWarningMessage,
  WARNING_MESSAGES,
  WarningText
} from "../../job/JobPanel";
import {useDispatchRulesDataContext} from "../../../providers/DispatchRulesDataProvider";
import {
  createJsonPref,
  extractJsonPref,
  extractSimplePref,
  usePreferenceContext
} from "../../../providers/PreferenceProvider";
import BadDataOverlay from "../../common/BadDataOverlay";
import DateFilter from "../../common/DateFilter";
import DoubleFieldCellRenderer, {DoubleFieldCellValue} from "../../common/cell-renderers/DoubleFieldCellRenderer";
import {
  ALLOWED_SAVED_PREFERENCE_SOURCES,
  COLORS,
  TenantPreferences,
  UserPreferences
} from "../../../constants/constants";
import {useDispatchGroupContext} from "../../../providers/DispatchGroupProvider";

import {useAppContext} from "../../../ApplicationContext";
import ServiceTypeCellRenderer from "../../common/cell-renderers/ServiceTypeCellRenderer";
import {ServerSideRowModelModule} from "@ag-grid-enterprise/server-side-row-model";
import {ColumnsToolPanelModule} from "@ag-grid-enterprise/column-tool-panel";
import {SetFilterModule} from "@ag-grid-enterprise/set-filter";
import StopStatusRenderer from "../../manifest/details/StopStatusRenderer";
import {jobStopStatusValueFormatter} from "../../manifest/details/utils/ManifestDetails.utils";
import {JobStopStatus, JobStopType, jobStopTypeToString} from "../../../utils/stops/StopUtils";
import {JobPiecesOrWeightCellRenderer} from "../../common/cell-renderers/JobPiecesAndWeightCellRenderer";
import {DetailsCellRenderer} from "../../common/cell-renderers/DetailsCellRenderer";
import {ContactCellRenderer, ContactValueGetter} from "../../common/cell-renderers/ContactCellRenderer";
import styled from "@emotion/styled";
import {
  getStopDateTimeCellValue,
  StopDateTimeType,
  DateTimeCellRenderer,
  getStopDateCellValue
} from "../../common/cell-renderers/DateTimeCellRenderer";
import {ColoredJobNumberCellRenderer} from "../../common/cell-renderers/ColoredJobNumberCellRenderer";
import {getHighestRankedColor, NO_COLOR_FILTER_VALUE} from "../../../utils/color-indicator/color-indicator.utils";
import {ConditionNames} from "../../settings/ColorSettings/ColorizedIndicators/RuleGenerator.service";
import {ManifestFilterType} from "../../manifest/list/components/manifest-filter/ManifestFilterState";
import {AssignmentViewState} from "../../../providers/AssignmentViewReducer";
import CustomerFilter from "../../common/CustomerFilter";
import {useFeatureFlagContext} from "../../../providers/FeatureFlagProvider";
import ColorCellRenderer from "../../common/cell-renderers/ColorCellRenderer";
import {Icon, Intent, Text} from "@blueprintjs/core";
import {copyToClipboard, getArrayFromField} from "../../../utils/General";
import {buildDispatchGroupFilter} from "../../../utils/FilterUtils";
import {isEqual} from "lodash";
import {LinkCellRenderer} from "../../common/cell-renderers/LinkCellRenderer";
import {TManifestDetailsDrawerOpen} from "../../../views/UnassignedJobsView";
import GridSettingsToolPanel from "../../common/GridSettingsToolPanel";
import {JobNumberTooltipUsage} from "../../common/cell-renderers/JobNumberCellRenderer";
import ActiveFilterDialog, {IActiveFilterDialogProps, IActiveFilterDialogRef} from "../../common/ActiveFilterDialog";
import ReadyDueFilter, {isValidFilter} from "../../common/ready-due-filter/ReadyDueFilter";
import ToolPanel from "../common/Toolpane";
import Search from "../../common/Search";
import {v4 as uniqueId} from "uuid";
import {getOptionCustomValue, TIME_VALUE_OPTIONS} from "../../common/ready-due-filter/FilterItem";
import {TReadyDueFilter, TReadyDueFilterItem} from "../../common/ready-due-filter/types";
import {hasSelectedDispatchStations, useDispatchStationContext} from "../../../providers/DispatchStationProvider";
import {CellRendererWithToolTip} from "../../common/cell-renderers/CellRendererWithToolTip";
import {useDispatchDataContext} from "../../../providers/DispatchDataProvider";
import {WarningIcon} from "../../common/icons";
import {getOrderSkillFilterParams, orderSkillsValueGetter} from "../../../utils/ag-grid/AGGridUtils";
import {useColorColumnSortControl} from "../../../hooks/useColorColumnSortControl";
import {useManifestDetailsState} from "../../../providers/ManifestDetailsProvider";
import {StopTypeIconCellRenderer} from "../../common/cell-renderers/StopTypeIconCellRenderer";
import {ColorIndicatorCellRenderer} from "../../common/cell-renderers/ColorIndicatorCellRenderer";
import {AgStop} from "../../common/types";
import {ETACellRenderer} from "../../common/cell-renderers/ETACellRenderer";
import {DispatchNotesProvider} from "../../../providers/DispatchNotesProvider";
import {fetchDispatchNotes} from "../../../services/DispatchNotesLookupService";
import {ConfirmationDialog} from "../../common/ConfirmationDialog";
import AdjustStopTime from "../../manifest/details/components/AdjustStopTime";
import {GridMenuContextText} from "../../manifest/details/components/ManifestDetailsContextMenu";
import {AdjustableStop} from "../../common/types";
import {
  AdjustTimeState,
  buildAdjustStopCalls,
  buildAdjustToastMessages,
  buildStopsByJobsFilter,
  getDistinctJobIds,
  resolveTargetStops
} from "../../../utils/stops/adjustStopTime.utils";
import {AppToasterTopLeft} from "../../../utils/toasters/toaster";

interface IGridProps {
  assignmentViewState: AssignmentViewState;
  onManifestDetailsDrawerOpen: TManifestDetailsDrawerOpen;
}

const TextFilter = ["filterColumn", "textFilter"];
const NumberFilter = ["filterColumn", "numberFilter"];
const DateFilterType = ["filterColumn", "dateFilter"];

const ColumnTypes: {[key: string]: ColDef<AgStop>} = {
  filterColumn: {
    suppressMenu: false,
    menuTabs: ["filterMenuTab"],
    filterParams: {
      buttons: ["apply", "reset"],
      closeOnApply: true
    }
  },
  textFilter: {
    filter: "agTextColumnFilter",
    filterParams: {
      filterOptions: ["contains", "notContains", "equals", "notEqual", "blank", "notBlank"]
    } as ITextFilterParams
  },
  numberFilter: {
    filter: "agNumberColumnFilter",
    filterParams: {
      filterOptions: [
        "equals",
        "notEqual",
        "lessThan",
        "lessThanOrEqual",
        "greaterThan",
        "greaterThanOrEqual",
        "inRange"
      ]
    }
  },
  setFilter: {
    filter: "agSetColumnFilter"
  },
  dateFilter: {filter: DateFilter, filterParams: {type: "range"}}
};

const SortableColumnFields = [
  "indicatorColor",
  "driver.name",
  "manifest.manifestDriverId",
  "job.jobNumber",
  "jobStopStatus",
  "stopType",
  "scheduledDateTime",
  "address",
  "dispatchZone",
  "job.service",
  "job.pieces",
  "job.weight",
  "name",
  "podName",
  "podDateTime",
  "arriveDateTime",
  "departDateTime",
  "lateDateTime",
  "earlyDateTime",
  "job.routeNumber",
  "job.site.code",
  "order.customer.name",
  "order.customer.customerCode",
  "order.auth",
  "order.alias",
  "job.alias",
  "order.caller",
  "order.declaredValue",
  "order.codAmount",
  "manifestSequence",
  "order.priority.description",
  "order.createDate",
  "order.user.userName"
];

const NotSortAndFilterColdef: ColDef = {sortable: false, type: "none"};

const DefaultColDef: ColDef = {
  resizable: true,
  suppressMenu: true,
  autoHeight: true,
  sortable: true,
  headerClass: "header-cell",
  cellClass: "body-cell",
  minWidth: 55,
  initialWidth: 90
};

const PinLeftColDef: ColDef = {
  lockPosition: "left",
  pinned: "left",
  lockPinned: true,
  suppressMovable: true,
  resizable: true
};

// On-demand fetch of every stop for the selected jobs (rows can span serverSide pages). DP-3051.
const SEARCH_STOPS_FOR_ADJUST = gql(/* GraphQL */ `
  query SearchStopsForAdjust($filter: SearchStopFilter, $limit: Int) {
    searchStops(filter: $filter, limit: $limit) {
      items {
        jobStopId
        scheduledDateTime
        lateDateTime
        job {
          jobId
          jobNumber
        }
        order {
          orderId
        }
      }
    }
  }
`);

export const addETAColumnsState = (columnState: ColumnState[]) => {
  const clonedColumnState = [...columnState];

  const etaColumnState = clonedColumnState.find((col) => col.colId === "eta");
  const jobNumberColumnIndex = clonedColumnState.findIndex((column) => column.colId === "job.jobNumber");
  if (!etaColumnState) {
    clonedColumnState.splice(
      jobNumberColumnIndex + 1,
      0,
      {
        colId: "eta"
      },
      {
        colId: "etaGeneratedDateTime"
      }
    );
  }

  return clonedColumnState;
};

const gridOptions = {
  getRowClass: (params: RowClassParams) => {
    if ((params.node.data as AgStop & {hide: boolean})?.hide) {
      return "hide-row";
    }
  }
};

export const DefaultReadyDueFilter: TReadyDueFilter = {
  items: [
    {
      id: uniqueId(),
      stopType: undefined,
      timeType: undefined,
      timeValue: undefined
    }
  ],
  customValues: {}
};

ModuleRegistry.registerModules([ServerSideRowModelModule, SetFilterModule, ColumnsToolPanelModule, MenuModule]);

const Grid = ({assignmentViewState, onManifestDetailsDrawerOpen}: IGridProps) => {
  const apolloClient = useApolloClient();
  const authState = useAuthState();
  const {appState} = useAppContext();

  const {tenantPreferences, userPrefsQueryRefetch, userPreferences, isUserPreferencesLoaded} = usePreferenceContext();
  const {rules, colorRank, getConfiguredEngine} = useDispatchRulesDataContext();
  const dispatchStationState = useDispatchStationContext();
  const {driverGroups} = useDispatchGroupContext();
  const {skills, sites, users, services, isDispatchDataLoaded} = useDispatchDataContext();

  const {
    colorSort: filterAndSortByColorsEnabled,
    etas: isETAFlagEnabled,
    skills: skillsEnabled,
    multipleSort: multipleSortEnabled,
    dispatchNote: dispatchNotesEnabled,
    backendColorRules: backendColorRulesEnabled
  } = useFeatureFlagContext();

  const [setUserPref] = useSetUserPrefMutation();
  const [updateJobStop] = useSaveClearJobStopsMutation();
  const [adjustStops, setAdjustStops] = useState<AdjustableStop[] | undefined>(undefined);

  const gridRef = useRef<AgGridReact<AgStop>>(null);

  // DP-3051: fetch every stop for the selected jobs (rows can span serverSide pages).
  const fetchStopsForJobs = useCallback(
    async (jobIds: number[]): Promise<AdjustableStop[]> => {
      if (jobIds.length === 0) {
        return [];
      }
      const res = await apolloClient.query<{searchStops: {items: AdjustableStop[]}}>({
        query: SEARCH_STOPS_FOR_ADJUST,
        variables: {filter: buildStopsByJobsFilter(jobIds), limit: 1000}
      });
      return res.data?.searchStops?.items ?? [];
    },
    [apolloClient]
  );

  // DP-3051: right-click Adjust / Mass Adjust. Label switches on the distinct-job count of the selection.
  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams<AgStop>): (string | MenuItemDef)[] => {
      const jobIds = getDistinctJobIds(resolveTargetStops(params.api.getSelectedRows(), params.node?.data));
      if (jobIds.length === 0) {
        return [];
      }
      const isMassAdjust = jobIds.length > 1;
      return [
        {
          name: isMassAdjust ? GridMenuContextText.MassAdjustJobStopTime : GridMenuContextText.AdjustJobStopTime,
          action: () => {
            fetchStopsForJobs(jobIds)
              .then((stops) => setAdjustStops(stops))
              .catch((error) => {
                log.error("Assigned stops - failed to load stops for adjustment", error);
                AppToasterTopLeft.show({
                  intent: Intent.DANGER,
                  icon: "asterisk",
                  message: "Unable to load stops for time adjustment"
                });
              });
          }
        }
      ];
    },
    [fetchStopsForJobs]
  );

  const handleAdjustStopTimesConfirm = useCallback(
    (state: AdjustTimeState) => {
      if (!adjustStops || adjustStops.length === 0) {
        return;
      }
      const {success, failed} = buildAdjustToastMessages(adjustStops);
      // DP-3051: fire one saveClearJobStops call per job (smallest jobStopId only); the
      // backend cascades to the job's other stops. All-or-nothing toast across the calls.
      Promise.all(
        buildAdjustStopCalls(adjustStops, state).map((saveClearJobStops) =>
          updateJobStop({variables: {saveClearJobStops}}).then((r) => {
            if (r.errors) {
              throw r.errors;
            }
            return r;
          })
        )
      )
        .then(() => {
          // DP-3051: clear the grid selection once the bump succeeds (keep it on failure so the user can retry).
          gridRef.current?.api.deselectAll();
          AppToasterTopLeft.show({intent: Intent.SUCCESS, icon: "tick", message: success});
        })
        .catch((reason) => {
          log.error(reason);
          AppToasterTopLeft.show({intent: Intent.DANGER, icon: "asterisk", message: failed});
        })
        .finally(() => setAdjustStops(undefined));
    },
    [adjustStops, updateJobStop]
  );

  const [gridReady, setGridReady] = useState<boolean>(false);

  const [datasource, setDatasource] = useState<IServerSideDatasource>();
  const [datasourceFailed, setDatasourceFailed] = useState<ApolloError | undefined>();

  const [gridContext, setGridContext] = useState<GridContext>({
    globalQuery: "",
    globalFilter: undefined,
    additionalFilters: []
  });
  const activeFilterDialogRef = useRef<IActiveFilterDialogRef>(null);
  const [gridHasActiveFilters, setGridHasActiveFilters] = useState(false);
  const [readyDueFilter, setReadyDueFilter] = useState<TReadyDueFilter>(DefaultReadyDueFilter);
  const [validReadyDueFilterItems, setValidReadyDueFilterItems] = useState<TReadyDueFilterItem[]>([]);

  const [warningMessage, setWarningMessage] = useState<WARNING_MESSAGES | undefined>();
  const [initializing, setInitializing] = useState<boolean>(true);
  // [DP-2993] Custom filter components (e.g. CustomerFilter) are instantiated asynchronously by
  // ag-Grid, so during restore getFilterModel() can transiently miss a saved filter column. Capture
  // the model we restored from and block persisting until every saved column is present again, so a
  // partial model can't overwrite (and wipe) the saved filter. Null = nothing to restore.
  const restoreFilterModelRef = useRef<Record<string, unknown> | null>(null);
  const [filterRestoreComplete, setFilterRestoreComplete] = useState<boolean>(false);

  const isTimezoneOfJobStop = extractSimplePref(tenantPreferences, TenantPreferences.timezoneOfJobStop, false)
    .value as boolean;

  const isUseETAService = extractSimplePref(tenantPreferences, TenantPreferences.useEtaService, true).value;

  const isETAEnabled = isETAFlagEnabled && isUseETAService;

  const {manifestDriverId} = useManifestDetailsState();
  useGridRefreshOnInterval(
    "Assigned Stops",
    gridRef,
    gridReady,
    datasourceFailed,
    undefined,
    manifestDriverId !== undefined
  );

  const sortingColumns =
    gridRef.current?.columnApi?.getAllGridColumns()?.filter((col) => {
      return col.getSort();
    }) ?? [];

  useColorColumnSortControl({
    gridRef,
    gridReady,
    isInitialized: !initializing,
    multipleSortEnabled,
    sortingColumns,
    sortableColumnFields: SortableColumnFields
  });

  const [searchETA] = useGetEtaLazyQuery({
    errorPolicy: "all"
  });

  const DateTimeValueGetter = useCallback(
    (params: ValueGetterParams<AgStop>, typeOfDateTime: StopDateTimeType) => {
      return getStopDateTimeCellValue({
        data: params.data as AgStop,
        is24HourFormat: appState.isTimeFormat24hr,
        isTimezoneOfJobStop: isTimezoneOfJobStop,
        typeOfDateTime: typeOfDateTime
      });
    },
    [appState.isTimeFormat24hr, isTimezoneOfJobStop]
  );

  const DateValueGetter = useCallback(
    (params: ValueGetterParams<AgStop>, typeOfDateTime: StopDateTimeType) => {
      return getStopDateCellValue({
        data: params.data as AgStop,
        isTimezoneOfJobStop: isTimezoneOfJobStop,
        typeOfDateTime: typeOfDateTime
      });
    },
    [isTimezoneOfJobStop]
  );

  const onManifestCellClicked = useCallback(
    (data: AgStop) => {
      if (!data.manifest?.manifestDriverId || !data.driver?.driverId) return;
      onManifestDetailsDrawerOpen(data.manifest?.manifestDriverId, data.driver?.driverId);
    },
    [onManifestDetailsDrawerOpen]
  );

  const colorColDefs: ColDef[] = useMemo(() => {
    return filterAndSortByColorsEnabled
      ? [
          {
            field: "indicatorColor",
            headerName: "Color",
            suppressColumnsToolPanel: true,
            headerClass: "header-cell header-cell-color",
            cellClass: "body-cell body-cell-color",
            initialWidth: 60,
            minWidth: 18,
            cellRenderer: ColorIndicatorCellRenderer,
            type: "filterColumn",
            filter: "agSetColumnFilter",
            filterParams: {
              cellRenderer: ColorCellRenderer,
              values: [NO_COLOR_FILTER_VALUE, ...(rules ?? []).map((rule) => rule.event.params?.color)],
              suppressMiniFilter: true
            },
            ...PinLeftColDef
          }
        ]
      : [
          {
            field: "indicatorColor",
            headerName: "Color",
            headerClass: "header-cell header-cell-color",
            cellClass: "body-cell body-cell-color",
            initialWidth: 18,
            minWidth: 18,
            cellRenderer: ColorIndicatorCellRenderer,
            ...PinLeftColDef,
            resizable: false
          }
        ];
  }, [filterAndSortByColorsEnabled, rules]);

  const skillsColumnDef: ColDef[] = useMemo(
    () =>
      skillsEnabled
        ? [
            {
              colId: "order.orderSkills.id",
              field: "order.orderSkills.id",
              headerName: "Skills",
              sortable: false,
              initialHide: true,
              type: "filterColumn",
              filter: "agSetColumnFilter",
              filterParams: getOrderSkillFilterParams(skills),
              valueGetter: (params: any) => {
                return orderSkillsValueGetter(params, skills);
              },
              cellRenderer: CellRendererWithToolTip
            } as ColDef
          ]
        : [],
    [skills, skillsEnabled]
  );

  const columnDefs: ColDef<AgStop>[] = useMemo(
    () =>
      [
        ...colorColDefs,
        {
          field: "driver.name",
          tooltipField: "driver.name",
          headerName: "Driver Name",
          initialWidth: 150,
          type: TextFilter,
          ...PinLeftColDef
        },
        {
          field: "manifest.manifestDriverId",
          tooltipField: "manifest.manifestDriverId",
          headerName: "Manifest #",
          cellClass: "body-cell text-blue text-bold",
          cellRenderer: LinkCellRenderer<AgStop>,
          cellRendererParams: {
            minimal: true,
            fontSize: 14,
            color: "#2563EB",
            onClick: onManifestCellClicked
          },
          initialWidth: 120,
          type: NumberFilter,
          ...PinLeftColDef
        },
        {
          field: "job.jobNumber",
          headerName: "Job #",
          initialWidth: 100,
          minWidth: 75,
          cellRenderer: ColoredJobNumberCellRenderer,
          cellRendererParams: {
            minimal: true,
            fontSize: 14,
            color: "#2563EB",
            usage: JobNumberTooltipUsage.ASSIGNED_STOPS_GRID,
            dispatchNotesEnabled: dispatchNotesEnabled
          },
          type: TextFilter,
          ...PinLeftColDef
        },
        ...((isETAEnabled
          ? [
              {
                field: "eta",
                headerName: "ETA",
                initialWidth: 120,
                cellRenderer: ETACellRenderer,
                cellRendererParams: {
                  style: {
                    fontSize: 13,
                    gap: 0
                  }
                },
                valueGetter: (params) => {
                  return getStopDateTimeCellValue({
                    data: params.data as AgStop,
                    is24HourFormat: appState.isTimeFormat24hr,
                    isTimezoneOfJobStop,
                    showPreposition: false,
                    typeOfDateTime: StopDateTimeType.ETA
                  });
                },
                filter: false,
                sortable: false
              },
              {
                field: "etaGeneratedDateTime",
                headerName: "ETA Generation Date/Time",
                initialWidth: 200,
                initialHide: true,
                cellRenderer: DateTimeCellRenderer,
                valueGetter: (params) => {
                  return getStopDateTimeCellValue({
                    data: params.data as AgStop,
                    is24HourFormat: appState.isTimeFormat24hr,
                    isTimezoneOfJobStop,
                    showPreposition: false,
                    typeOfDateTime: StopDateTimeType.ETA_GENERATION_TIME
                  });
                },
                filter: false,
                sortable: false
              }
            ]
          : []) as ColDef<AgStop>[]),
        {
          field: "jobStopStatus",
          headerName: "Status",
          cellRenderer: StopStatusRenderer,
          cellStyle: {"--ag-cell-horizontal-padding": "4px", display: "flex", justifyContent: "center"},
          type: ["filterColumn", "setFilter"],
          filterParams: {
            values: Object.values(JobStopStatus),
            valueFormatter: jobStopStatusValueFormatter
          }
        },
        {
          field: "stopType",
          headerName: "Type",
          initialWidth: 70,
          cellRenderer: StopTypeIconCellRenderer,
          type: ["filterColumn", "setFilter"],
          filterParams: {
            values: Object.values(JobStopType),
            valueFormatter: jobStopTypeToString
          }
        },
        {
          field: "scheduledDateTime",
          headerName: "Scheduled Time",
          initialWidth: 150,
          cellRenderer: DateTimeCellRenderer,
          valueGetter: (params) => {
            return DateTimeValueGetter(params, StopDateTimeType.SCHEDULED_TIME);
          },
          type: DateFilterType
        },
        {
          field: "address",
          headerName: "Address",
          initialWidth: 150,
          cellRenderer: DoubleFieldCellRenderer,
          valueGetter: (params) => {
            return {
              upperField: {
                value: params.data.address,
                tooltip: params.data.address
              },
              lowerField: {
                value: `${params.data.city}, ${params.data.state}, ${params.data.zip}`,
                tooltip: `${params.data.city}, ${params.data.state}, ${params.data.zip}`,
                style: {
                  color: "#6B7280"
                }
              }
            } as DoubleFieldCellValue;
          },
          type: TextFilter,
          cellStyle: {
            cursor: "pointer"
          },
          cellClass: "body-cell assigned-stop-address-cell",
          onCellClicked: (params: CellClickedEvent<AgStop>) => {
            if (!params.data) return;
            const address = [params.data.address, params.data.city, params.data.state, params.data.zip]
              .filter(Boolean)
              .join(", ");
            copyToClipboard(address).then(() => {
              /*Noop*/
            });
          }
        },
        {
          field: "dispatchZone",
          headerName: "Zone",
          tooltipField: "dispatchZone",
          type: TextFilter
        },
        {
          field: "job.service",
          headerName: "SVT",
          initialWidth: 100,
          cellRenderer: ServiceTypeCellRenderer,
          type: ["filterColumn"],
          filter: "agSetColumnFilter",
          filterParams: {
            cellRenderer: ServiceTypeCellRenderer,
            values: services
          }
        },
        {
          field: "job.pieces",
          headerName: "Pieces",
          cellRenderer: JobPiecesOrWeightCellRenderer,
          type: NumberFilter
        },
        {
          field: "job.weight",
          headerName: "Weight",
          cellRenderer: JobPiecesOrWeightCellRenderer,
          type: NumberFilter
        },
        {
          field: "name",
          tooltipField: "name",
          headerName: "Stop Name",
          initialWidth: 120,
          type: TextFilter
        },
        {
          field: "podName",
          tooltipField: "podName",
          headerName: "POD Name",
          initialWidth: 120,
          type: TextFilter
        },
        {
          field: "podDateTime",
          headerName: "POD Date",
          initialWidth: 120,
          cellRenderer: DateTimeCellRenderer,
          valueGetter: (params) => {
            return DateTimeValueGetter(params, StopDateTimeType.POD_TIME);
          },
          type: DateFilterType
        },
        {
          field: "arriveDateTime",
          headerName: "Arrival Time",
          initialWidth: 120,
          cellRenderer: DateTimeCellRenderer,
          valueGetter: (params) => {
            return DateTimeValueGetter(params, StopDateTimeType.ARRIVAL_TIME);
          },
          type: DateFilterType
        },
        {
          field: "departDateTime",
          headerName: "Departure Time",
          initialWidth: 150,
          cellRenderer: DateTimeCellRenderer,
          valueGetter: (params) => {
            return DateTimeValueGetter(params, StopDateTimeType.DEPARTURE_TIME);
          },
          type: DateFilterType
        },
        {
          field: "lateDateTime",
          headerName: "Late Time",
          initialWidth: 120,
          cellRenderer: DateTimeCellRenderer,
          valueGetter: (params) => {
            return DateTimeValueGetter(params, StopDateTimeType.LATE_TIME);
          },
          type: DateFilterType
        },
        {
          field: "earlyDateTime",
          headerName: "Early Time",
          initialWidth: 120,
          cellRenderer: DateTimeCellRenderer,
          valueGetter: (params) => {
            return DateTimeValueGetter(params, StopDateTimeType.EARLY_TIME);
          },
          type: DateFilterType
        },
        {
          colId: "completedTime",
          headerName: "Completed Time",
          initialWidth: 120,
          cellRenderer: DateTimeCellRenderer,
          valueGetter: (params) => {
            return DateTimeValueGetter(params, StopDateTimeType.COMPLETED_TIME);
          },
          ...NotSortAndFilterColdef
        },
        {
          field: "job.routeNumber",
          tooltipField: "job.routeNumber",
          headerName: "Route Number",
          initialWidth: 150,
          type: TextFilter
        },
        {
          field: "job.site.code",
          tooltipField: "job.site.code",
          headerName: "Site",
          type: ["filterColumn"],
          filter: "agSetColumnFilter",
          filterParams: {
            values: sites
          }
        },
        {
          field: "order.customer.name",
          tooltipField: "order.customer.name",
          headerName: "Customer Account",
          initialWidth: 170,
          type: "filterColumn",
          filter: CustomerFilter
        },
        {
          field: "order.customer.customerCode",
          tooltipField: "order.customer.customerCode",
          headerName: "Account #",
          initialWidth: 120,
          type: TextFilter
        },
        {
          field: "order.auth",
          tooltipField: "order.auth",
          headerName: "Order Auth",
          initialWidth: 120,
          type: TextFilter
        },
        {
          field: "order.alias",
          tooltipField: "order.alias",
          headerName: "Order Alias",
          initialWidth: 120,
          type: TextFilter
        },
        {
          field: "job.alias",
          tooltipField: "job.alias",
          headerName: "Job Alias",
          initialWidth: 120,
          initialHide: true,
          type: TextFilter
        },
        {
          field: "order.caller",
          tooltipField: "order.caller",
          headerName: "Caller",
          initialWidth: 120,
          type: TextFilter
        },
        {
          field: "order.declaredValue",
          tooltipField: "order.declaredValue",
          headerName: "Declared Value",
          type: NumberFilter,
          initialWidth: 150,
          initialHide: true
        },
        {
          field: "order.codAmount",
          tooltipField: "order.codAmount",
          headerName: "COD Amount",
          type: NumberFilter,
          initialWidth: 150,
          initialHide: true
        },
        {
          field: "manifestSequence",
          tooltipField: "manifestSequence",
          headerName: "Sequence #",
          initialWidth: 120,
          type: NumberFilter,
          initialHide: true
        },
        {
          field: "see",
          headerName: "Contact",
          initialWidth: 150,
          cellRenderer: ContactCellRenderer,
          valueGetter: ContactValueGetter,
          initialHide: true,
          ...NotSortAndFilterColdef
        },
        {
          field: "order.notes",
          tooltipField: "order.notes",
          headerName: "Order Notes",
          initialWidth: 150,
          initialHide: true,
          ...NotSortAndFilterColdef
        },
        {
          field: "note",
          tooltipField: "note",
          headerName: "Stop Notes",
          initialWidth: 150,
          initialHide: true,
          ...NotSortAndFilterColdef
        },
        {
          field: "order.customer.notes",
          tooltipField: "order.customer.notes",
          headerName: "Customer Notes",
          initialWidth: 150,
          initialHide: true,
          ...NotSortAndFilterColdef
        },
        {
          field: "order.priority.description",
          tooltipField: "order.priority.description",
          headerName: "Order Priority",
          initialHide: true,
          initialWidth: 120,
          type: TextFilter
        },
        {
          field: "order.createDate",
          headerName: "Created Date",
          initialWidth: 120,
          initialHide: true,
          cellRenderer: CellRendererWithToolTip,
          valueGetter: (params) => {
            return DateValueGetter(params, StopDateTimeType.ORDER_CREATED_DATE);
          },
          type: DateFilterType
        },
        {
          field: "order.user.userName",
          headerName: "User",
          initialHide: true,
          type: "filterColumn",
          filter: "agSetColumnFilter",
          filterParams: {
            values: users
          }
        },
        ...skillsColumnDef,
        {
          colId: "details",
          headerName: "Details",
          cellRenderer: DetailsCellRenderer,
          ...NotSortAndFilterColdef
        }
      ] as ColDef[],
    [
      colorColDefs,
      onManifestCellClicked,
      dispatchNotesEnabled,
      isETAEnabled,
      services,
      sites,
      users,
      skillsColumnDef,
      appState.isTimeFormat24hr,
      isTimezoneOfJobStop,
      DateTimeValueGetter,
      DateValueGetter
    ]
  );

  const getRowId = useCallback((params) => params.data.jobStopId.toString(), []);

  const handleGridReady = useCallback(() => {
    setGridReady(true);
  }, []);

  const datasourceFailCallback = useCallback((error: ApolloError | undefined) => {
    setDatasourceFailed(error);
  }, []);

  // maxBlocksInCache cannot purge the cache here (SSRM only honours it with infinite scroll, not
  // pagination, and autoHeight disables purging too), so blocks stay cached and the datasource's
  // getData never re-runs when navigating back. A failed (bad-data) block is cached in a failedLoad
  // state and renders default "ERR" cells on revisit without re-running getData, so drive the
  // overlay off the current page's row state instead: show it over a failed page (the opaque
  // overlay covers the ERR cells), hide it once the page has real rows.
  const onPaginationChanged = useCallback((event: PaginationChangedEvent<AgStop>) => {
    const startRow = event.api.paginationGetCurrentPage() * event.api.paginationGetPageSize();
    const firstRowOnPage = event.api.getDisplayedRowAtIndex(startRow);
    if (firstRowOnPage?.failedLoad) {
      event.api.showNoRowsOverlay();
    } else if (firstRowOnPage?.data) {
      event.api.hideOverlay();
    }
  }, []);

  const getColorIndicator = useCallback(
    async (stop: AgStop) => {
      if (rules && stop?.manifest) {
        const colors: string[] = [];

        const engine = getConfiguredEngine(
          {manifest: stop.manifest, stop: stop},
          rules.filter((rule) => rule.conditions.name !== ConditionNames.MANIFESTONLY)
        );
        if (engine === undefined) return;

        const results = await engine.run();
        if (results.events.length > 0) {
          colors.push(results.events[0].params?.color);
        }

        stop.indicatorColor = colorRank ? getHighestRankedColor(colors, colorRank) : undefined;
      }
    },
    [colorRank, getConfiguredEngine, rules]
  );

  const buildIndicatorColors = useCallback(
    async (params: IServerSideGetRowsParams, newRows: AgStop[]) => {
      const promises = newRows.map(async (incomingRow) => {
        await getColorIndicator(incomingRow);
      });

      const results = await Promise.allSettled(promises);

      results.forEach((result) => {
        if (result.status === "rejected") {
          log.warn("Error getting color indicator", result.reason);
        }
      });
    },
    [getColorIndicator]
  );

  const sortAndFilterByColors = useCallback(
    async (params: IServerSideGetRowsParams, newRows: AgStop[], res: ApolloQueryResult<any>) => {
      if (filterAndSortByColorsEnabled) {
        const {sortModel, filterModel} = params.request;
        const filterByColors = filterModel?.indicatorColor;
        const sortByColors = sortModel.find((item) => item.colId === "indicatorColor");
        if (filterByColors) {
          filterByColors.values[filterByColors.values.findIndex((item: string) => item === "No Color")] = undefined;
          newRows.forEach((row, index) => {
            if (!filterByColors.values.includes(row.indicatorColor)) {
              newRows[index].hide = true;
            }
          });
        }
        if (sortByColors) {
          const sortDirection = sortByColors.sort === "asc" ? 1 : -1;
          newRows.sort((a, b) => {
            return (
              (getColorPriority(rules, a.indicatorColor) - getColorPriority(rules, b.indicatorColor)) * sortDirection
            );
          });
        }
        const hideRows = newRows.filter((row) => row.hide);

        hideRows.forEach((hideRow) => {
          const hideRowIndex = newRows.findIndex((row) => row.jobStopId === hideRow.jobStopId);
          newRows.splice(hideRowIndex, 1);
          newRows.push(hideRow);
        });

        if (res.data.searchStops.total > 100) {
          const warningMsg = getWarningMessage(Boolean(sortByColors), Boolean(filterByColors));
          setWarningMessage(warningMsg);
        } else {
          setWarningMessage(undefined);
        }
      }
    },
    [filterAndSortByColorsEnabled, rules]
  );

  const decorateETA = useCallback(
    async (params: IServerSideGetRowsParams, newRows: AgStop[]) => {
      if (isETAEnabled) {
        const manifestDriverIds = Array.from(
          new Set(newRows.map((row) => row.manifest?.manifestDriverId).filter(Boolean) as number[])
        );
        const {data: etaResult} = await searchETA({variables: {manifestDriverIds: manifestDriverIds}});
        const etaData = etaResult?.getETA?.items ?? [];
        newRows.forEach((row) => {
          const manifestETA = etaData.find((item) => item.manifestDriverId === row.manifest?.manifestDriverId);
          const jobStopETA = manifestETA?.items.find((item) => item.jobStopId === row.jobStopId);
          if (jobStopETA) {
            row.eta = jobStopETA.eta;
            row.generatedDateTime = jobStopETA.generatedDateTime;
            row.etaSource = jobStopETA.etaSource;
          }
        });
      }
    },
    [isETAEnabled, searchETA]
  );

  const decorateDispatchNotes = useCallback(
    async (params: IServerSideGetRowsParams, newRows: AgStop[]) => {
      if (dispatchNotesEnabled) {
        const orderIds = Array.from(new Set(newRows.map((row) => row.order?.orderId).filter(Boolean) as number[]));
        if (orderIds.length > 0) {
          const notesMap = await fetchDispatchNotes(apolloClient, orderIds);
          newRows.forEach((row) => {
            const orderId = row.order?.orderId;
            if (orderId != null && notesMap.has(orderId)) {
              row.order = {...row.order!, orderEvents: notesMap.get(orderId)!};
            }
          });
        }
      }
    },
    [dispatchNotesEnabled, apolloClient]
  );

  const handleGlobalSearch = useCallback(
    (query: string) => {
      if (gridReady) {
        if (gridContext.globalQuery != query) {
          setGridContext({...gridContext, globalQuery: query});
        }
      }
    },
    [gridContext, gridReady]
  );

  const onToggleToolPane = useCallback(
    (key: string) => {
      if (gridReady) {
        if (gridRef.current!.api.getOpenedToolPanel() === key) {
          gridRef.current!.api.closeToolPanel();
        } else {
          gridRef.current!.api.openToolPanel(key);
        }
      }
    },
    [gridReady]
  );

  const updateColumn = useCallback(
    (e: ColumnMovedEvent<AgStop> | SortChangedEvent<AgStop> | ColumnVisibleEvent<AgStop>) => {
      const {source, columnApi} = e;
      const previousColumnState = extractJsonPref(userPreferences, UserPreferences.assignedStopsGridColumnState)?.value;

      if (!ALLOWED_SAVED_PREFERENCE_SOURCES.includes(source)) {
        log.debug("reverted visible state");
        const revertVisibleState: ColumnState[] = getArrayFromField(previousColumnState).map(
          ({colId, hide}: ColumnState) => ({
            colId,
            hide
          })
        );
        columnApi.applyColumnState({
          state: revertVisibleState
        });
      }

      const newColumnState = columnApi.getColumnState();
      if (gridReady && !datasourceFailed && !isEqual(previousColumnState, newColumnState)) {
        setUserPref({
          variables: {
            name: UserPreferences.assignedStopsGridColumnState,
            input: createJsonPref(newColumnState, true)
          }
        }).then(() => {
          userPrefsQueryRefetch?.();
        });
      }
    },
    [userPreferences, gridReady, datasourceFailed, setUserPref, userPrefsQueryRefetch]
  );

  const onColumnMoved = useCallback(
    (e: ColumnMovedEvent<AgStop>) => {
      if (!e.finished || e.source === "api") return;
      updateColumn(e);
    },
    [updateColumn]
  );

  const onColumnSortChanged = useCallback(
    (e: SortChangedEvent<AgStop>) => {
      if (e.source === "api") return;
      updateColumn(e);
    },
    [updateColumn]
  );

  const onColumnVisible = useCallback(
    (e: ColumnVisibleEvent<AgStop>) => {
      if (e.source === "api") return;
      updateColumn(e);
      e.api.refreshServerSide();
    },
    [updateColumn]
  );

  const onFilterChanged = useCallback(
    (event: FilterChangedEvent<AgStop>) => {
      setGridHasActiveFilters(Object.keys(event.api.getFilterModel()).length > 0);
      const filterState = extractJsonPref(userPreferences, UserPreferences.assignedStopsGridFilterState)?.value;
      const newFilterState = event.api.getFilterModel();

      // [DP-2993] While restoring, custom filters mount asynchronously and the model can be missing
      // saved columns. Don't persist until every saved filter column has been restored, otherwise a
      // partial model overwrites and wipes the saved filter (the Customer Account filter loss).
      if (!filterRestoreComplete) {
        const expected = restoreFilterModelRef.current;
        const columnApi = gridRef.current?.columnApi;
        // Saving stays blocked until every saved filter whose column still exists is present again.
        // A saved key whose column no longer exists is ignored, so saving is never permanently
        // blocked (no timeout fallback needed).
        const allRestored =
          !expected || Object.keys(expected).every((key) => !columnApi?.getColumn(key) || key in newFilterState);
        if (allRestored) {
          setFilterRestoreComplete(true);
        }
        return;
      }

      if (!isEqual(filterState, newFilterState) && !initializing)
        try {
          setUserPref({
            variables: {
              name: UserPreferences.assignedStopsGridFilterState,
              input: createJsonPref(newFilterState, true)
            }
          }).then(() => {
            userPrefsQueryRefetch?.();
          });
        } catch (error) {
          log.debug(error);
        }
    },
    [initializing, filterRestoreComplete, setGridHasActiveFilters, setUserPref, userPreferences, userPrefsQueryRefetch]
  );

  const handleAdditionalFilterChange = useCallback((name: string, getter: () => StopFilter) => {
    setGridContext((prev) => {
      return {
        ...prev,
        additionalFilters: prev.additionalFilters
          ?.filter((item) => item.name !== name)
          .concat({name: name, getter: getter})
      };
    });
  }, []);

  const handleReadyDueFiltersChange = useCallback((filter: TReadyDueFilter) => {
    setReadyDueFilter(filter);
  }, []);

  const onRestoreDefaults = useCallback(
    (justRestoreFilters?: boolean) => {
      gridRef.current?.api.setFilterModel(null);
      const promises = [
        setUserPref({
          variables: {name: UserPreferences.assignedStopsGridFilterState, input: createJsonPref(null, true)}
        })
      ];
      if (!justRestoreFilters) {
        promises.push(
          setUserPref({
            variables: {name: UserPreferences.assignedStopsGridColumnState, input: createJsonPref(null, true)}
          })
        );
      }
      Promise.all(promises).then(() => {
        userPrefsQueryRefetch?.();
      });

      if (!justRestoreFilters) {
        gridRef.current?.columnApi.resetColumnState();
      }

      gridRef.current?.api.onFilterChanged();
    },
    [setUserPref, userPrefsQueryRefetch]
  );

  const onRemoveFilters = useCallback(() => {
    onRestoreDefaults(true);
  }, [onRestoreDefaults]);

  const sideBar = useMemo(
    () => ({
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressColumnSelectAll: true,
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true
          }
        },
        {
          id: "settings",
          labelDefault: "Settings",
          labelKey: "settings",
          iconKey: "menu",
          toolPanel: GridSettingsToolPanel,
          toolPanelParams: {
            onRestoreDefaults: onRestoreDefaults
          }
        }
      ],
      defaultToolPanel: "customStats"
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const activeFilterDialogProps: IActiveFilterDialogProps = useMemo(
    () => ({
      message:
        "To view all assigned stops, please clear any active filters in the table header that may be limiting your stop list.",
      title: "Active Filters Present",
      icon: <Icon icon="filter" color="green" size={21} />,
      actionButton: {
        label: "Remove Filter(s)",
        action: () => {
          onRemoveFilters();
          setReadyDueFilter(DefaultReadyDueFilter);
        }
      }
    }),
    [onRemoveFilters]
  );

  // On initial load, apply saved column state and filter state from user preferences
  useEffect(() => {
    if (gridReady && initializing && isDispatchDataLoaded && isUserPreferencesLoaded) {
      let savedColumnState = extractJsonPref(userPreferences, UserPreferences.assignedStopsGridColumnState).value;
      if (savedColumnState) {
        if (isETAEnabled) {
          savedColumnState = addETAColumnsState(savedColumnState);
        }
        savedColumnState = addNewColumnStateAfter(savedColumnState, "job.alias", "order.alias");
        gridRef.current!.columnApi.applyColumnState({state: savedColumnState, applyOrder: true});
      }

      const columnFilters = extractJsonPref(userPreferences, UserPreferences.assignedStopsGridFilterState);
      const hasFilterState = columnFilters.value && Object.keys(columnFilters.value).length > 0;
      if (hasFilterState) {
        // [DP-2993] Remember what we're restoring; saving stays blocked until every one of these
        // columns is present in the grid model again (custom filters mount asynchronously). Saved
        // keys whose column no longer exists are ignored by the onFilterChanged check.
        restoreFilterModelRef.current = columnFilters.value as Record<string, unknown>;
        gridRef.current!.api.setFilterModel(columnFilters.value);
      } else {
        // Nothing to restore — saving can proceed immediately.
        setFilterRestoreComplete(true);
      }

      let savedReadyDueFilter = extractJsonPref(
        userPreferences,
        UserPreferences.assignedStopsGridReadyDueFilterState,
        DefaultReadyDueFilter
      ).value as TReadyDueFilter;
      const validFilterItems = savedReadyDueFilter.items.filter(isValidFilter);
      if (validFilterItems.length === 0) {
        savedReadyDueFilter = DefaultReadyDueFilter;
      }
      savedReadyDueFilter.items = savedReadyDueFilter.items.map((item) => ({
        ...item,
        getFilterValue: TIME_VALUE_OPTIONS.find((option) => option.value === item.timeValue)?.getFilterValue
      }));

      setReadyDueFilter(savedReadyDueFilter);

      if (validFilterItems.length > 0 || hasFilterState) {
        activeFilterDialogRef.current?.onOpen();
      }
      setInitializing(false);
    }
  }, [gridReady, initializing, userPreferences, isDispatchDataLoaded, isUserPreferencesLoaded, isETAEnabled]);

  useEffect(() => {
    let dipatchGroupIds: (number | string)[] = [];
    let fromSelectedStation = false;
    if (assignmentViewState.manifestFilters?.hasFilter(ManifestFilterType.DRIVER_GROUP)) {
      dipatchGroupIds = Array.from(
        assignmentViewState.manifestFilters.manifestFilters.get(ManifestFilterType.DRIVER_GROUP)?.value ?? []
      );
    } else if (hasSelectedDispatchStations(dispatchStationState)) {
      dipatchGroupIds = driverGroups.map((item) => item.dispatchGroupId as number | string);
      fromSelectedStation = true;
    }
    setGridContext((prev) => ({
      ...prev,
      globalFilter:
        dipatchGroupIds.length === 0 && !fromSelectedStation
          ? undefined
          : (buildDispatchGroupFilter("manifest_dispatchGroupId", Array.from(new Set(dipatchGroupIds))) as StopFilter)
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentViewState.manifestFilters, driverGroups, setGridContext, dispatchStationState]);

  // When ready due filter changes, update the grid context and persist the change to user preferences
  useEffect(() => {
    if (gridRef.current) {
      const newFilter: TReadyDueFilter = {
        ...readyDueFilter,
        items: readyDueFilter.items.filter(isValidFilter).map((item) => ({
          ...item,
          customValue: getOptionCustomValue(readyDueFilter.customValues, item)
        }))
      };
      if (isEqual(validReadyDueFilterItems, newFilter.items)) {
        return;
      }
      setValidReadyDueFilterItems(newFilter.items);
      const readyDueFiltterGetter = (): StopFilter => {
        return {
          or: newFilter.items.map((filterItem) => ({
            and: [
              {stopType: {match: filterItem.stopType}},
              {
                [filterItem.timeType as string]: {
                  lte: filterItem?.getFilterValue?.(getOptionCustomValue(newFilter.customValues, filterItem))[1]
                } as ModelStringFilterInput
              }
            ]
          }))
        };
      };
      handleAdditionalFilterChange("readyDue", readyDueFiltterGetter);
      setUserPref({
        variables: {
          name: UserPreferences.assignedStopsGridReadyDueFilterState,
          input: {
            value: JSON.stringify(newFilter)
          }
        }
      }).then(() => {
        userPrefsQueryRefetch?.();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyDueFilter]);

  // When datasource is created or recreated, set it on the grid
  useEffect(() => {
    if (gridReady) {
      setDatasource(
        createStopsServerSideDatasource({
          client: apolloClient,
          defaultFields: [
            "manifest.manifestStatus",
            "manifest.vehicleType.vehicleTypeId",
            "jobStopId",
            "stopType",
            "timeZone",
            "scheduledDateTime",
            "earlyDateTime",
            "lateDateTime",
            "arriveDateTime",
            "departDateTime",
            "podDateTime",
            "name",
            "address",
            "city",
            "state",
            "zip",
            "note",
            "see",
            "phone",
            "order.orderId",
            "order.service",
            "order.customer.notes",
            "order.customer.customerId",
            "order.notes",
            "order.priority.orderPriorityId",
            "driver.driverId",
            "job.jobId",
            "job.site.siteId",
            "jobStopStatus",
            ...(backendColorRulesEnabled ? ["indicatorColor", "indicatorRank"] : [])
          ],
          excludeFields: [
            ...(backendColorRulesEnabled ? [] : ["indicatorColor"]),
            "details",
            "completedTime",
            "eta",
            "etaGeneratedDateTime"
          ],
          onDatasourceFail: datasourceFailCallback,
          decorateResults: [
            decorateETA,
            decorateDispatchNotes,
            ...(backendColorRulesEnabled ? [] : [buildIndicatorColors, sortAndFilterByColors])
          ],
          tokenExpiry: authState.expiresAt ? new Date(authState.expiresAt) : undefined,
          backendColorRules: backendColorRulesEnabled
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apolloClient, gridReady, authState.expiresAt, datasourceFailCallback]);

  // When grid context changes, refresh the server side datasource to apply the new filters
  useEffect(() => {
    if (gridReady && !initializing) {
      gridRef.current!.api.onFilterChanged();
    }
  }, [gridReady, gridContext, initializing]);

  return (
    <DispatchNotesProvider>
      <Container>
        <ActiveFilterDialog ref={activeFilterDialogRef} {...activeFilterDialogProps} />
        <Toolbar>
          <div style={{display: "flex", gap: "16px"}}>
            <Search
              onSearch={handleGlobalSearch}
              isConstrainedByFilters={gridHasActiveFilters || validReadyDueFilterItems.length > 0}
              idPrefix="assigned-stops"
              width="300px"
            />
            <ReadyDueFilter
              filter={readyDueFilter}
              onFilterChange={handleReadyDueFiltersChange}
              maxNumberOfFilters={3}
            />
          </div>
          <ToolPanel onToggleToolPanel={onToggleToolPane} />
        </Toolbar>
        {warningMessage && (
          <WarningText style={{padding: "4px 16px 8px"}}>
            <WarningIcon />
            <Text ellipsize>{warningMessage}</Text>
          </WarningText>
        )}
        <GridContainer className="ag-theme-alpine">
          <AgGridReact<AgStop>
            className={"stops-panel-grid"}
            ref={gridRef}
            sideBar={sideBar}
            onGridReady={handleGridReady}
            getRowId={getRowId}
            defaultColDef={DefaultColDef}
            columnDefs={columnDefs}
            context={gridContext}
            suppressCellFocus
            columnTypes={ColumnTypes}
            onColumnVisible={onColumnVisible}
            onFilterChanged={onFilterChanged}
            onSortChanged={onColumnSortChanged}
            onColumnMoved={onColumnMoved}
            onColumnResized={onColumnMoved}
            enableCellTextSelection
            ensureDomOrder
            rowSelection={"multiple"}
            rowMultiSelectWithClick={true}
            getContextMenuItems={getContextMenuItems}
            rowModelType={"serverSide"}
            serverSideDatasource={datasource}
            onPaginationChanged={onPaginationChanged}
            pagination
            paginationPageSize={100}
            cacheBlockSize={100}
            animateRows
            headerHeight={40}
            rowHeight={44}
            gridOptions={gridOptions}
            maintainColumnOrder
            rowStyle={rowStyle}
            suppressCsvExport
            suppressMultiSort={!multipleSortEnabled}
            noRowsOverlayComponent={BadDataOverlay}
            noRowsOverlayComponentParams={{entity: "stop"}}
          />
        </GridContainer>
        <ConfirmationDialog
          testIdPrefix={"assigned-stops-adjust-time-job"}
          isVisible={adjustStops !== undefined}
          icon={<></>}
          header={"Adjust Stop Time"}
          body={
            <AdjustStopTime
              stops={adjustStops ?? []}
              onConfirm={handleAdjustStopTimesConfirm}
              onClose={() => setAdjustStops(undefined)}
            />
          }
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          handleYesClicked={() => {}}
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          handleNoClicked={() => {}}
          handleClose={() => setAdjustStops(undefined)}
          useFooter={false}
        />
      </Container>
    </DispatchNotesProvider>
  );
};

export default Grid;

const rowStyle: RowStyle = {
  userSelect: "none",
  fontSize: "12px"
};

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: ${COLORS.BG_GRAY};
`;

const GridContainer = styled.div`
  flex: 1;
  font-family: Roboto;

  /* Inset the overlay by the header height (top) and pagination footer height (bottom) so the
     bad-data overlay covers the grid body only, leaving the column headers and pager usable.
     Values mirror the DP-2849 Jobs grids (JobPanel.css / route-planning/styles/grids.css). */
  .ag-overlay {
    pointer-events: all;
    position: absolute;
    top: 49px;
    right: 0;
    bottom: 49px;
    left: 0;
  }

  .ag-side-bar .ag-side-buttons {
    display: none;
  }

  .header-cell {
    color: #4b5563;
    padding: 0 12px;
  }

  .body-cell {
    font-size: 14px;
    padding: 0 12px;
    color: #111827;
  }

  .header-cell-color {
    color: #4b5563;
    padding: 0 0 0 4px !important;
  }

  .body-cell-color {
    padding: 0 4px !important;
  }

  .ag-header-cell-text {
    font-size: 12px;
    text-transform: uppercase;
  }

  .ag-pinned-left-cols-container,
  .ag-pinned-left-header {
    border-right: none;
  }

  .ag-cell.ag-cell-last-left-pinned:not(.ag-cell-range-right):not(.ag-cell-range-single-cell) {
    border: none;
  }

  .text-blue {
    color: #2563eb;
  }

  .text-bold {
    font-weight: 500;
  }

  .ag-cell {
    .bp5-popover-target {
      display: block;
    }
  }
`;

const Toolbar = styled.div`
  height: 44px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
`;
